import { useCallback, useMemo, useState } from "react";
import { VStack, HStack, Text, IconButton, Icon, Link as ChakraLink } from "@chakra-ui/react";
import { FaMap } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import Popover from "@hydrologiq/shared/chakra/Popover";
import AlertsList from "./AlertsList";
import { alertSeverityConfig, getAlertSeverityHighest, AlertSeverityType } from "../AlertSeverityBadge";

interface Alert {
  id: string;
  name: string;
  type?: string;
  severity: string;
  status: string;
  message: string;
  firedAt: string;
}

interface AlertContext {
  customer?: string;
  site?: string;
  asset?: string;
}

interface AlertsPopoverProps {
  alerts: Alert[];
  totalCount: number;
  triggerLabel?: string;
  context: AlertContext;
  loading?: boolean;
  error?: string;
  onAlertStatusUpdate?: (alertId: string, newStatus: string) => void;
  refetchQueries?: string[];
  placement?: "right-end" | "left-end" | "top-end" | "bottom-end" | "top" | "bottom" | "left" | "right";
  width?: string;
}

function AlertsPopover({
  alerts,
  totalCount,
  triggerLabel,
  context,
  loading = false,
  error,
  onAlertStatusUpdate,
  refetchQueries = [],
  placement = "bottom-end",
  width = "500px"
}: AlertsPopoverProps) {
  const [open, setOpen] = useState(false);

  const handleAlertStatusUpdate = useCallback(
    (alertId: string, newStatus: string) => {
      onAlertStatusUpdate?.(alertId, newStatus);
    },
    [onAlertStatusUpdate]
  );

  const mapHref = useMemo(() => {
    const params = new URLSearchParams();

    if (context.asset) {
      params.set("asset", context.asset);
    } else {
      if (context.customer) {
        params.set("customer", context.customer);
      }
      if (context.site) {
        params.set("site", context.site);
      }
    }

    const url = `/alerts${params.toString() ? `?${params.toString()}` : ""}`;
    return url;
  }, [context]);


  const hasAlerts = totalCount > 0;

  const highestSeverity = useMemo(() => {
    const severities = alerts.map((a) => a.severity);
    return getAlertSeverityHighest(severities);
  }, [alerts]);

  const triggerContent = useMemo(() => {
    if (!hasAlerts) return totalCount.toString();
    const config = highestSeverity ? alertSeverityConfig[highestSeverity as AlertSeverityType] : undefined;
    const SeverityIcon = config?.icon;
    return (
      <HStack gap={1}>
        {SeverityIcon && <Icon as={SeverityIcon} color={config?.iconColor} boxSize="14px" />}
        <ChakraLink variant="secondary">{totalCount}</ChakraLink>
      </HStack>
    );
  }, [hasAlerts, highestSeverity, totalCount]);

  return (
    <Popover
      placement={placement}
      additionalContent={triggerContent}
      ariaLabel={`View ${totalCount} alerts for ${triggerLabel}`}
      width={width}
      open={open}
      onOpenChange={setOpen}
      disabled={!hasAlerts && !loading && !error}
      showArrow={true}
      closeOnOutside
    >
      <VStack align="stretch" gap={3}>


        {/* Loading State */}
        {loading && <Text>Loading alerts...</Text>}

        {/* Error State */}
        {error && <Text color="red.500">Error loading alerts: {error}</Text>}

        {/* No Alerts State */}
        {!loading && !error && totalCount === 0 && <Text>No active alerts</Text>}

        {/* Alerts List */}
        {!loading && !error && hasAlerts && (
          <AlertsList
            alerts={alerts}
            totalCount={totalCount}
            onAlertStatusUpdate={handleAlertStatusUpdate}
            refetchQueries={refetchQueries}
            maxHeight="none"
            updateNote="Updated from alerts popover"
            canResolveAlerts={true}
            currentAlertLink={mapHref}
          />
        )}
      </VStack>
    </Popover>
  );
}

export default AlertsPopover;